import { useState } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Zap, TrendingUp, CheckCircle, Package } from "lucide-react";
import { maintenanceService } from "../../services/maintenance/planMaestroService";
import { toast } from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";

interface ProveedorMasivoSelectorProps {
  otId: number;
  actividades: any[];
  selectedIds?: number[];
  onSuccess: () => void;
}

export function ProveedorMasivoSelector({
  otId,
  actividades,
  selectedIds = [],
  onSuccess,
}: ProveedorMasivoSelectorProps) {
  const [open, setOpen] = useState(false);
  const [analizando, setAnalizando] = useState(false);
  const [mejoresOpciones, setMejoresOpciones] = useState<any[]>([]);
  const [aplicando, setAplicando] = useState(false);

  const handleAnalizar = async () => {
    try {
      setAnalizando(true);
      const response = await maintenanceService.analizarMejoresProveedores(
        otId,
        selectedIds.length > 0 ? selectedIds : undefined
      );
      setMejoresOpciones(response.data || []);
    } catch (error) {
      toast.error("Error al analizar proveedores");
      console.error(error);
    } finally {
      setAnalizando(false);
    }
  };

  const handleAplicarMasivo = async (opcion: any) => {
    try {
      setAplicando(true);
      await maintenanceService.aplicarProveedorMasivo(otId, {
        proveedor_id: opcion.proveedor.id,
        items_ids: opcion.items.map((i: any) => i.actividad_id),
        tipo: opcion.tipo,
        paquete_id: opcion.paquete_id,
      });
      toast.success("Asignación realizada con éxito");
      setOpen(false);
      onSuccess();
    } catch (error) {
      toast.error("Error al aplicar proveedor masivo");
      console.error(error);
    } finally {
      setAplicando(false);
    }
  };

  const totalAnalizados = selectedIds.length || actividades.filter(a => !a.proveedor_seleccionado_id).length;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          setOpen(true);
          handleAnalizar();
        }}
        className={`group border-indigo-200 text-indigo-700 hover:bg-indigo-50 transition-all ${selectedIds.length > 0 ? "bg-indigo-50 border-indigo-400" : ""
          }`}
      >
        <Zap className={`h-4 w-4 mr-2 ${selectedIds.length > 0 ? "fill-indigo-500" : ""}`} />
        <span className="font-medium">Asignación Inteligente</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col p-0 border border-slate-200 shadow-xl">
          <DialogHeader className="p-6 pb-2 border-b bg-white">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-50 rounded-lg">
                <Zap className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <DialogTitle className="text-lg font-bold text-slate-900">
                  Sourcing Inteligente
                </DialogTitle>
                <DialogDescription className="text-slate-500 text-sm">
                  {selectedIds.length > 0
                    ? `Analizando ${selectedIds.length} ítems seleccionados...`
                    : "Analizando actividades sin proveedor..."}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-slate-50/30">
            <AnimatePresence mode="wait">
              {analizando ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="h-10 w-10 border-2 border-indigo-100 border-t-indigo-600 rounded-full animate-spin mb-4" />
                  <p className="text-sm font-medium text-slate-500">Buscando mejores ofertas...</p>
                </div>
              ) : mejoresOpciones.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl border border-dashed border-slate-200">
                  <TrendingUp className="h-10 w-10 text-slate-200 mx-auto mb-3" />
                  <h3 className="text-sm font-bold text-slate-800">No hay opciones disponibles</h3>
                  <p className="text-xs text-slate-500 max-w-xs mx-auto mt-1">
                    Asegúrese de tener cotizaciones vigentes cargadas para estos ítems.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {mejoresOpciones.map((opcion, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`group bg-white border rounded-xl overflow-hidden shadow-sm transition-all hover:border-indigo-300 ${idx === 0 ? "border-indigo-200 ring-1 ring-indigo-50" : "border-slate-200"
                        }`}
                    >
                      <div className="p-4">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex gap-4">
                            <div className={`h-11 w-11 rounded-lg flex items-center justify-center ${opcion.tipo === 'PAQUETE'
                              ? 'bg-purple-50 text-purple-600'
                              : 'bg-blue-50 text-blue-600'
                              }`}>
                              {opcion.tipo === 'PAQUETE' ? <Package size={22} /> : <CheckCircle size={22} />}
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-0.5">
                                <h4 className="font-bold text-base text-slate-900">
                                  {opcion.tipo === 'PAQUETE' ? opcion.nombre_paquete : opcion.proveedor.razon_social}
                                </h4>
                                {idx === 0 && (
                                  <Badge className="bg-indigo-50 text-indigo-700 hover:bg-indigo-50 border-indigo-100 text-[10px] py-0 px-2 font-bold uppercase tracking-wider">
                                    Recomendado
                                  </Badge>
                                )}
                              </div>
                              <p className="text-[11px] text-slate-500 font-medium">
                                {opcion.tipo === 'PAQUETE' ? `Proveedor: ${opcion.proveedor.razon_social}` : `Calificación: ${opcion.proveedor.calificacion || '4.5'}/5`}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-0.5">Costo Total</span>
                            <p className="text-lg font-bold text-slate-900">
                              S/ {Number(opcion.costo_total).toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                            </p>
                            <Badge variant="outline" className="mt-1 bg-emerald-50 text-emerald-700 border-none text-[10px] font-bold">
                              Ahorro: S/ {Number(opcion.ahorro_total).toFixed(2)}
                            </Badge>
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-2 mb-4">
                          <div className="bg-slate-50 px-3 py-2 rounded-lg border border-slate-100">
                            <p className="text-[9px] font-bold text-slate-400 uppercase mb-1">Cobertura</p>
                            <p className="text-sm font-bold text-slate-700">{opcion.items_cubiertos} de {totalAnalizados}</p>
                          </div>
                          <div className="bg-slate-50 px-3 py-2 rounded-lg border border-slate-100">
                            <p className="text-[9px] font-bold text-slate-400 uppercase mb-1">Score</p>
                            <p className="text-sm font-bold text-slate-700">{opcion.score_proveedor || 85}</p>
                          </div>
                          <div className="bg-slate-50 px-3 py-2 rounded-lg border border-slate-100">
                            <p className="text-[9px] font-bold text-slate-400 uppercase mb-1">Confianza</p>
                            <p className="text-sm font-bold text-emerald-600">{opcion.metricas?.porcentaje_puntualidad?.toFixed(0) || 95}%</p>
                          </div>
                        </div>

                        <div className="bg-slate-50/50 p-3 rounded-lg border border-slate-100 mb-4">
                          <p className="text-[9px] font-bold text-slate-400 uppercase mb-2">Items incluidos</p>
                          <div className="flex flex-wrap gap-1.5 line-clamp-2">
                            {opcion.items.slice(0, 5).map((item: any, i: number) => (
                              <span key={i} className="bg-white px-2 py-0.5 rounded border border-slate-200 text-[10px] font-medium text-slate-600">
                                {item.nombre}
                              </span>
                            ))}
                            {opcion.items.length > 5 && (
                              <span className="text-[10px] text-slate-400 font-medium px-1">+{opcion.items.length - 5} más</span>
                            )}
                          </div>
                        </div>

                        <Button
                          className={`w-full py-5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all shadow-sm ${idx === 0
                            ? "bg-indigo-600 hover:bg-indigo-700 text-white"
                            : "bg-white border text-slate-700 hover:bg-slate-50"
                            }`}
                          onClick={() => handleAplicarMasivo(opcion)}
                          disabled={aplicando}
                        >
                          {aplicando ? "Procesando..." : "Aplicar Selección"}
                        </Button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </AnimatePresence>
          </div>

          <DialogFooter className="p-4 bg-white border-t border-slate-100 sm:justify-between items-center">
            <p className="text-[10px] text-slate-400 font-medium italic">
              * Para asignar un ítem individualmente, use el botón de editar en la lista.
            </p>
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)} className="text-xs font-bold text-slate-500">
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
